import React, { useState, useEffect, useContext } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { useV2MonthYear } from "../../hooks/useV2MonthYear";
import "./Page.css";
import "./RewardsSales.css";
import YearNavigator from "./YearNavigator";
import { ValueCard, CircularChartCard, SalesByTypeTable } from "../../components/v2/cards";

const RewardsSales = () => {
  const keycloak = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const { month, year: selectedYear, setMonthYear } = useV2MonthYear();

  const [additiveSalesStats, setAdditiveSalesStats] = useState({
    totalRevenue: 0,
    monthlyVariation: 0,
    monthlyRevenue: 0,
    additiveSalesByType: {
      total: 0,
      details: [],
    },
    topAdditiveProducts: [],
  });

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "--";
    return typeof num === "number" ? num.toLocaleString(locale) : num;
  };

  const formatLegendValue = (value, segment, total) => {
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
    return `${percentage}% (${value}/${total})`;
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "--";
    return typeof amount === "number"
      ? `${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
      : amount;
  };

  const fetchAdditiveSalesStats = () => {
    if (!keycloak || !keycloak.isAuthenticated || !keycloak.user?.access_token || !hId) {
      setHasError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    const params = new URLSearchParams({
      lang,
    });
    if (selectedYear) params.append("year", String(selectedYear));

    const url = `/api/hotels/${hId}/additive-sales/stats?${params.toString()}`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errData) => {
            throw new Error(errData.message || `HTTP error! status: ${response.status}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        setAdditiveSalesStats(data);
        setHasError(false);
        setLoading(false);
      })
      .catch((error) => {
        console.error("RewardsSales - Error fetching data:", error);
        setHasError(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (keycloak && keycloak.isAuthenticated && keycloak.user && hId) {
      fetchAdditiveSalesStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.isAuthenticated, hId, selectedYear, lang]);

  const handleYearChange = ({ year }) => {
    setMonthYear({ month, year });
  };

  const topProductsData = {
    total: additiveSalesStats.additiveSalesByType?.total || 0,
    details: additiveSalesStats.additiveSalesByType?.details || [],
  };

  const salesByTypeData = (additiveSalesStats.topAdditiveProducts || []).map((product) => {
    let iconColor = "#91B0D8";
    const nameLower = (product.name || "").toLowerCase();
    if (nameLower.includes("boutique") || nameLower.includes("shop")) {
      iconColor = "#CFA7A0";
    }

    return {
      name: product.name || translate("v2_rewards_sales_unnamed", lang),
      iconColor,
      usageCount: product.usageCount || 0,
      totalUpsales: formatCurrency(product.totalUpsales || 0),
      upsalesPerSale: formatCurrency(product.upsalesPerSale || 0),
    };
  });

  return (
    <div className="v2-page">
      <div className="v2-page__header">
        <h1>{translate("v2_rewards_sales_title", lang)}</h1>
        <YearNavigator year={selectedYear} onChange={handleYearChange} />
      </div>
      {hasError && (
        <div style={{ padding: "20px", textAlign: "center", color: "#d32f2f" }}>
          <p>{translate("v2_error_load_data", lang)}</p>
        </div>
      )}
      <div className="rewards-sales__content">
        <div className="rewards-sales__top-cards">
          <ValueCard
            title={translate("v2_rewards_sales_annual", lang)}
            subtitle={translate("v2_rewards_sales_revenue_sub", lang)}
            value={additiveSalesStats.totalRevenue || 0}
            unit="€"
            showUnit={true}
            variation={additiveSalesStats.monthlyVariation || 0}
            badgeValue={additiveSalesStats.monthlyRevenue || 0}
            badgeUnit="€"
            variationLabel={translate("v2_rewards_sales_last_month", lang)}
            loading={loading}
            valueColor="#3C5544"
          />

          <div className="rewards-sales__card">
            <CircularChartCard
              title={translate("v2_rewards_sales_top_products", lang)}
              subtitle={translate("v2_rewards_sales_top_sub", lang)}
              data={topProductsData}
              colors={["#B0C996", "#697F6A"]}
              startWithMaxSegment={true}
              startRotation={-90}
              drawOrder="reverse"
              legendPosition="right"
              formatValue={formatNumber}
              formatLegendValue={formatLegendValue}
              showLegendPercentage={true}
              loading={loading}
            />
          </div>
        </div>

        <div className="rewards-sales__card rewards-sales__card-large">
          <SalesByTypeTable
            title={translate("v2_rewards_sales_by_type", lang)}
            subtitle={translate("v2_rewards_sales_by_type_sub", lang)}
            data={salesByTypeData}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export default RewardsSales;
